import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import moment from "moment";

import { get } from "lodash";

import {
  Grid,
  Table,
  Paper,
  Button,
  Dialog,
  Toolbar,
  Divider,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  DialogTitle,
  TableContainer,
  CircularProgress,
} from "@material-ui/core";

import EditIcon from "@material-ui/icons/Edit";

import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";

import { getShouldDisplayDataRetentionMessage } from "views/Pages/MSPPortal/utils";

import Request from "lib/api/Request";

import LoadingTable from "../Components/LoadingTable";
import ActionDialog from "views/Components/ActionDialog";
import SimpleModelForm from "views/Components/SimpleModelForm";

import countries from "i18n-iso-countries";

countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const blumiraEditionsUrl =
  "https://blumira.zendesk.com/hc/en-us/articles/5178628123283-About-the-Blumira-editions";

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    height: 250,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  titleBar: {
    width: "fit-content",
    flex: "1 1 100%",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  orgName: {
    paddingBottom: theme.spacing(4),
  },
  toolBar: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  tableLeftHeaderCell: {
    "text-align": "right",
    "font-weight": 700,
    "white-space": "nowrap",
  },
  alert: {
    margin: `0 24px`,
    "& p": {
      margin: 0,
    },
    alignItems: "center",
    "& a": {
      color: theme.palette.primary.main,
    },
  },
}));

export const generalOrgFields = ["name", "shortName", "webUrl"];

export const superadminOrgFields = [
  "orgType",
  "market",
  "license",
  "licenseFeatures",
  "notes",
];

export const childAccountFields = ["parentId"];

export const blumiraAgentSuperadminFields = ["agentSeatCount"];

export const getOrgFieldsForMapping = (
  isParentOrg,
  isSuperadmin,
  isMarketTypeMSP,
  isBlumiraAgentEnabled
) => {
  let fieldsToReturn = generalOrgFields;

  // if this is a super admin, there are additional
  // fields to display as well as the potential to
  // display a parent id field if the current org is
  // not a parent organization
  if (isSuperadmin) {
    fieldsToReturn = fieldsToReturn.concat(superadminOrgFields);

    // if this is a super admin and the org being edited
    // has Blumira Agent enabled, display the seat count
    if (isBlumiraAgentEnabled)
      fieldsToReturn = fieldsToReturn.concat(blumiraAgentSuperadminFields);

    // if this is a super admin and they are viewing
    // an organization that *could* be a child account
    if (!isParentOrg && isMarketTypeMSP)
      fieldsToReturn = fieldsToReturn.concat(childAccountFields);
  }

  return fieldsToReturn;
};

export const ORG_TYPES = [
  { value: "normal", label: "Paying Customer" },
  { value: "poc", label: "Customer Trial" },
  { value: "demo", label: "Internal Blumira Demo Account" },
  { value: "internal", label: "Internal" },
];

export const MARKETS = [
  { value: 0, label: "Direct" },
  { value: 10, label: "MSSP" },
  { value: 20, label: "MSP" },
  { value: 30, label: "Self-Service" },
  { value: 40, label: "Reseller" },
  { value: 50, label: "Referral" },
];

export const LICENSE_TYPES = [
  { value: "FREE", label: "Free" },
  { value: "M365", label: "Microsoft 365" },
  { value: "CLOUD", label: "Cloud" },
  { value: "ADVANCED", label: "Advanced" },
];

export const LICENSE_FEATURE_TYPES = [
  { value: "LOGSHIPPING", label: "Blumira Agent" },
  { value: "AUTOBOT", label: "Auto Isolation" },
];

const getUserTimezone = () => {
  let userTz = null;

  try {
    const payload = JSON.parse(localStorage.getItem("idTokenPayload"));
    userTz = payload["https://b5a.io/geoip"].time_zone;
  } catch (err) {
    userTz = moment.tz.guess();
  }

  return userTz;
};

export const getIsBlumiraAgentEnabled = (model) => {
  // list of features lives on config as well but should
  // be present at 'licenseFeature' from FEA translation
  const licenseFeatures = get(model, ["licenseFeatures"], []);

  // LOGSHIPPING indicates access to blumira agent
  const isBlumiraAgentFeatureFlagPresent = licenseFeatures.find(
    (feature) => feature === "LOGSHIPPING"
  );

  // return bool
  return !!isBlumiraAgentFeatureFlagPresent;
};

export const getIsParentIdModifiedAndValid = (model) => {
  // get array of modified properties
  const modifiedParams = Object.keys(model._original_params);

  // determine if user is editing parentId
  const wasParentIdModified = modifiedParams.some((p) => p === "parentId");

  // if user did not modify parentID
  // no need to continue with logic
  if (!wasParentIdModified) return false;

  // validation error will appear in UI if value isn't
  // UUID but we should not even try to query if the length
  // doesn't match the expected 36 char length of a UUID
  const parentId = get(model, ["parentId"], "");
  if (parentId && parentId.split("").length !== 36) return false;

  // if the parent id was modified and the length
  // of the entered id is enough for a UUID we can
  // try to query for a matching parent organization
  return true;
};

export const EditOrgDialog = (props) => {
  const {
    isSaving,
    openForm,
    userModel,
    handleClose,
    isSuperadmin,
    locationModel,
    timezones = [],
    handleSaveClick,
    selectedOrgModel,
  } = props;

  const classes = useStyles();

  const [isParent, setIsParent] = useState(false);
  const [parentOrg, setParentOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isMarketTypeMSP, setIsMarketTypeMSP] = useState(false);
  const [isBlumiraAgentEnabled, setIsBlumiraAgentEnabled] = useState(false);
  const [
    shouldDataRetentionMessageBeDisplayed,
    setShouldDataRetentionMessageBeDisplayed,
  ] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await handleGetChildAndParentOrgs(selectedOrgModel);
      setIsLoading(false);
    };

    // in order to fetch child orgs we need an id additionaly
    // if a model does not have an id it is a net new org
    if (selectedOrgModel?.id) fetchData();
  }, [selectedOrgModel]);

  const timezoneOptions = timezones
    .map((tz) => ({ value: tz.name, label: tz.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // FIELDS
  const organizationFields = !isSuperadmin
    ? {
        name: {
          type: "text",
          default: "",
          label: "Name",
        },
        shortName: {
          xs: 6,
          type: "text",
          default: "",
          label: "Short Name",
        },
        webUrl: {
          xs: 6,
          type: "text",
          default: "",
          label: "Web Domain",
        },
      }
    : {
        name: {
          type: "text",
          default: "",
          label: "Name",
        },
        shortName: {
          xs: 6,
          type: "text",
          default: "",
          label: "Short Name",
        },
        webUrl: {
          xs: 6,
          type: "text",
          default: "",
          label: "Web Domain",
        },
        orgType: {
          xs: 6,
          type: "select",
          lookup: ORG_TYPES,
          default: "poc",
          label: "Account Type",
        },
        market: {
          xs: 6,
          type: "select",
          lookup: MARKETS,
          default: 0,
          label: "Market",
        },
        license: {
          type: "select",
          lookup: LICENSE_TYPES,
          label: "License",
        },
        licenseFeatures: {
          type: "multiselect",
          lookup: LICENSE_FEATURE_TYPES,
          label: "License Features",
          default: [],
        },
        notes: {
          type: "text",
          default: "",
          label: "Internal Notes",
        },
        // if viewing in the context of administering an org allow
        // for edit otherwise parent id is read only even for SAs
        parentId: {
          type: "text",
          default: null,
          label: "Parent ID",
          readOnly: !props.isAdministeringOrg,
          plainText: !props.isAdministeringOrg,
        },
        agentSeatCount: {
          type: "number",
          label: "Agent Seat Count",
        },
      };

  const locationFields = {
    name: {
      xs: 6,
      type: "text",
      default: "Headquarters",
      label: "Name",
    },
    timezone: {
      xs: 6,
      type: "select",
      label: "Timezone",
      lookup: timezoneOptions,
      default: getUserTimezone(),
    },
    city: {
      xs: 6,
      type: "text",
      default: "",
      label: "City",
    },
    country: {
      xs: 6,
      type: "select",
      default: "US",
      label: "Country",
      lookup: Object.entries(countries.getNames("en"))
        .map(([key, val]) => ({ value: key, label: val }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
  };

  const userFields = {
    firstName: {
      xs: 6,
      type: "text",
      default: "",
      label: "First Name",
    },
    lastName: {
      xs: 6,
      type: "text",
      default: "",
      label: "Last Name",
    },
    email: {
      type: "text",
      default: "",
      label: "Email",
    },
  };

  const getLayout = (modelType, model) => {
    switch (modelType) {
      case "location":
        return Object.keys(locationFields).map((field) => {
          return {
            field: field,
            xs: locationFields[field].xs ? locationFields[field].xs : 12,
          };
        });
      case "user":
        return Object.keys(userFields).map((field) => {
          return {
            field: field,
            xs: userFields[field].xs ? userFields[field].xs : 12,
          };
        });
      default: {
        let keysForFieldMap = getOrgFieldsForMapping(
          isParent,
          isSuperadmin,
          isMarketTypeMSP,
          isBlumiraAgentEnabled
        );
        return keysForFieldMap.map((field) => {
          return {
            field: field,
            xs: organizationFields[field].xs
              ? organizationFields[field].xs
              : 12,
          };
        });
      }
    }
  };

  const getFields = (modelType) => {
    let fieldsToReturn = organizationFields;
    switch (modelType) {
      case "location":
        fieldsToReturn = locationFields;
        break;
      case "user":
        fieldsToReturn = userFields;
        break;
      default:
        fieldsToReturn = organizationFields;
        break;
    }
    return fieldsToReturn;
  };

  const handleGetChildAndParentOrgs = async (model) => {
    // no need to make MSP-related requests on orgs
    // that are not of the MSP market type (20)
    // additionally, only Blumira superadmins are
    // able to edit MSP child/parent relationships
    if (model?.market === 20 && props.superadmin) {
      try {
        // fetch children of editing org to validate that this org is not
        // a parent to avoid a parent org being a child in a differnt org
        const childOrgs = await new Request("/org", [
          { field: "parentId", value: model.id },
        ]).get();

        // if no orgs are returned or the model has an existing parentId the org being
        // edited is not a parent and can therefore be a child of a different org
        if (!childOrgs.length || model.parentId) setIsParent(false);
        else if (model.market === 20 && !model.parentId) setIsParent(true);

        // if we do not yet have a parent org or the user changed the
        // parentID and it does not match current parent org in state
        if ((parentOrg && parentOrg.id !== model.parentId) || !parentOrg) {
          const existingParentOrg = await new Request("/org", [
            { field: "id", value: model.parentId },
          ]).get();
          setParentOrg(existingParentOrg[0]);
        }

        // if we have success do not display err banner
        setErrorMessage(null);
      } catch (e) {
        // display an error message to super admins
        setErrorMessage(
          "We had an issue loading parent or child account information. Please double check the desired parent ID."
        );
        setIsLoading(false);
      }
    }
  };

  const onValidate = (model, fields) => {
    const isBlumiraAgentEnabledOnModel = getIsBlumiraAgentEnabled(model);

    // get license value to compare against existing license
    const newLicenseField = get(fields, ["license", "value"], undefined);

    if (newLicenseField) {
      // compare against existing license
      const newShouldShowMessageBoolean = getShouldDisplayDataRetentionMessage(
        newLicenseField,
        model
      );

      // show informational alert for data retention if appropriate
      setShouldDataRetentionMessageBeDisplayed(newShouldShowMessageBoolean);
    }

    setIsBlumiraAgentEnabled(isBlumiraAgentEnabledOnModel);

    // only handle parent/child logic if
    // current organziation is market MSP
    if (model.market === 20) {
      setIsMarketTypeMSP(true);
      const isParentIdModifiedAndValid = getIsParentIdModifiedAndValid(model);

      // if this is a super admin who is administering
      // an org, update UI to reflect potential changes
      if (isParentIdModifiedAndValid) handleGetChildAndParentOrgs(model);
      // if modified but unable to retrieve parent
      // account based on improper UUID structure
      // reflect this in the UI
      else setParentOrg(false);
    }

    return fields;
  };

  const shouldAlertBeDisplayed = () => {
    // if we have an error to dsiplay, do so
    if (errorMessage) return true;

    // if the current org is not of type MSP or this
    // is not an SA administering, do not display
    if (!isMarketTypeMSP || !props.isAdministeringOrg) return false;

    // if this is a parent org
    if (isMarketTypeMSP && isParent) return true;

    // if market type is MSP and this
    // org is not a parent, display
    if (isMarketTypeMSP && !isParent) return true;

    // if we have a parentOrg loaded that
    // means this is a child account, display
    if (parentOrg) return true;

    // if any other situation, do not display
    return false;
  };

  const getAlertTextBeDisplayed = () => {
    if (errorMessage) return errorMessage;

    // this org is a parent
    if (isParent)
      return "This organization *is* a parent, NFR account and cannot be made a child account";

    // super admin has entered a parent id
    // that matches the id of an org in our db
    if (parentOrg)
      return `As is, this organization will be a child of the organization named: ${parentOrg.name}`;

    // this org is not a parent
    if (!isParent)
      return "This organization is not a parent, NFR account and can safely be made a child account";
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={openForm}
      fullWidth={true}
      maxWidth="md"
      datacy={"editOrganizationModal"}
    >
      <DialogTitle id="simple-dialog-title">
        {selectedOrgModel.id
          ? `Edit ${selectedOrgModel.name}`
          : `New Organization, Location, User`}
      </DialogTitle>
      {shouldAlertBeDisplayed() && (
        <Alert
          severity={errorMessage ? "error" : "info"}
          className={classes.alert}
        >
          {getAlertTextBeDisplayed()}
        </Alert>
      )}
      {shouldDataRetentionMessageBeDisplayed && (
        <Alert
          severity={errorMessage ? "error" : "info"}
          className={classes.alert}
        >
          <p>
            Your license selection will move you to a shorter data retention
            period. Any logging data outside of your new retention period will
            be immediately purged. If you have any questions about this, please
            reference{" "}
            <a
              target={"_blank"}
              href={blumiraEditionsUrl}
              rel={"noopener noreferrer"}
            >
              About the Blumira Editions
            </a>{" "}
            or email{" "}
            <a
              target={"_blank"}
              href={"mailto:support@blumira.com"}
              title={"support@blumira.com"}
              rel={"noopener noreferrer"}
            >
              support@blumira.com
            </a>{" "}
            before saving your changes.
          </p>
        </Alert>
      )}
      {isLoading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <div>
          <SimpleModelForm
            spacing={1}
            isSaving={isSaving}
            onValidate={onValidate}
            model={selectedOrgModel}
            onClose={handleClose}
            layout={getLayout("org")}
            fields={getFields("org")}
            actions={
              !locationModel
                ? [
                    {
                      title: "Cancel",
                      type: "button",
                      action: handleClose,
                    },
                    {
                      title: "Save",
                      type: "submit",
                    },
                  ]
                : null
            }
          />
          {!!locationModel && (
            <SimpleModelForm
              spacing={1}
              isSaving={isSaving}
              model={locationModel}
              onClose={handleClose}
              layout={getLayout("location")}
              fields={getFields("location")}
            />
          )}
          {!!userModel && (
            <SimpleModelForm
              spacing={1}
              isSaving={isSaving}
              model={userModel}
              onClose={handleClose}
              actions={[
                {
                  title: "Cancel",
                  type: "button",
                  action: handleClose,
                },
                {
                  title: "Save",
                  type: "button",
                  action: handleSaveClick,
                },
              ]}
              layout={getLayout("user")}
              fields={getFields("user")}
            />
          )}
        </div>
      )}
    </Dialog>
  );
};

const OrganizationPageView = (props) => {
  const [openForm, setOpenForm] = useState(false);
  const [openAction, setOpenAction] = useState(false);
  const [selectedOrgModel, setSelectedOrgModel] = useState(false);
  const [org, setOrg] = useState(null);
  const classes = useStyles();

  useEffect(() => {
    if (props.ready) {
      if (props.org.length > 0) {
        setOrg(props.org[0]);
      } else {
        setOrg(null);
      }
    }
  }, [props.org, props.ready]);

  const handleEditOrgClick = () => {
    setSelectedOrgModel(org);
    setOpenForm(true);
  };

  const handleClose = (action = null) => {
    setOpenForm(false);
    setSelectedOrgModel(false);
    window.location.reload();
  };

  const handleDelete = () => {
    selectedOrgModel.delete().then(() => {
      props.handleDeleteDone();
    });
  };

  const leftHeader = {
    component: "th",
    width: 1,
    variant: "head",
    className: classes.tableLeftHeaderCell,
  };

  return (
    <div className="dashboard organization-page">
      <div id="organization-information">
        {!props.ready || !org ? (
          <LoadingTable isMaterial />
        ) : (
          <Paper>
            <Toolbar variant="dense" className={classes.toolBar}>
              <Grid container alignItems="center" className={classes.titleBar}>
                <Typography variant="h6" id="tableTitle" component="div">
                  Organization Details
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Button
                  size="small"
                  color="primary"
                  onClick={handleEditOrgClick}
                  startIcon={<EditIcon />}
                  datacy={"simpleTableRowIconButton"}
                >
                  Edit
                </Button>
              </Grid>
            </Toolbar>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell {...leftHeader}>Name:</TableCell>
                    <TableCell>{org?.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell {...leftHeader}>Short Name:</TableCell>
                    <TableCell>{org?.shortName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell {...leftHeader}>Web Domain:</TableCell>
                    <TableCell>{org?.webUrl}</TableCell>
                  </TableRow>
                  {!!props.superadmin && (
                    <TableRow>
                      <TableCell {...leftHeader}>Account Type:</TableCell>
                      <TableCell>
                        {
                          (
                            ORG_TYPES.find(
                              (ot) => ot.value === org?.orgType
                            ) || { label: org?.orgType }
                          ).label
                        }
                      </TableCell>
                    </TableRow>
                  )}
                  {!!props.superadmin && (
                    <TableRow>
                      <TableCell {...leftHeader}>Market:</TableCell>
                      <TableCell>
                        {
                          (
                            MARKETS.find((m) => m.value === org?.market) || {
                              label: org?.market,
                            }
                          ).label
                        }
                      </TableCell>
                    </TableRow>
                  )}
                  {!!props.superadmin && (
                    <TableRow>
                      <TableCell {...leftHeader}>Internal Notes:</TableCell>
                      <TableCell>{org?.notes}</TableCell>
                    </TableRow>
                  )}
                  {!!props.superadmin && (
                    <TableRow>
                      <TableCell {...leftHeader}>Parent ID:</TableCell>
                      <TableCell>{org?.parentId}</TableCell>
                    </TableRow>
                  )}
                  {!!props.superadmin && getIsBlumiraAgentEnabled(org) && (
                    <TableRow>
                      <TableCell {...leftHeader}>Agent Seat Count:</TableCell>
                      <TableCell>{org?.agentSeatCount}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <ActionDialog
          open={openAction}
          title={`Delete ${selectedOrgModel && selectedOrgModel.name}`}
          description="Are you sure you want to delete this organization?"
          actions={[
            {
              title: "Cancel",
              action: () => setOpenAction(false),
            },
            {
              title: "Delete",
              action: handleDelete,
            },
          ]}
        />

        <EditOrgDialog
          openForm={openForm}
          handleClose={handleClose}
          isSuperadmin={!!props.superadmin}
          selectedOrgModel={selectedOrgModel}
          isAdministeringOrg={props.isAdministeringOrg}
        />
      </div>
    </div>
  );
};

export const OrganizationComponent = OrganizationPageView;

const mapStateToProps = (state) => {
  const isSuperadmin = get(state, "session.settings.user.superadmin", false);

  return {
    superadmin: isSuperadmin,
    sessionOrgId: state.page.payload.orgId,
  };
};

OrganizationPageView.propTypes = {
  orgs: PropTypes.array,
  payload: PropTypes.shape({}),
};

OrganizationPageView.defaultProps = {
  org: [],
  isFetching: true,
  orgsError: null,
  payload: { toplevel: "settings", secondlevel: "organization" },
};

export default connect(mapStateToProps)(OrganizationPageView);
